import React, { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import FormikInputField from 'components/forms/fields/FormikInputField';
import Button from 'components/buttons/Button';
import axios from 'data/axios';
import toast from 'react-hot-toast';
import { useRouter } from 'next/router';
import AutocompleteFormikField from 'components/forms/fields/AutocompleteFormikField';
import Area from 'models/Area';
import { setUser } from 'contexts/AppActions';
import AppContext from 'contexts/AppContext';
import mapUserProfile from 'models/mapping/mapUserProfile';
import FormikCheckboxField from 'components/forms/fields/FormikCheckboxField';
import Link from 'next/link';
import documents from 'config/documents';
import useUser from 'hooks/useUser';
import { useLocalStorage } from 'react-use';
import { loginRedirectStorageKey } from 'data/constants';

interface RegistrationFormState {
    first_name: string;
    last_name: string;
    area_id: number;
    newsletter: boolean;
}

const initialValues: RegistrationFormState = {
    first_name: '',
    last_name: '',
    area_id: null,
    newsletter: true
};

const schema = Yup.object().shape({
    first_name: Yup.string().required('Vnesi svoje ime'),
    last_name: Yup.string().required('Vnesi svoj priimek'),
    newsletter: Yup.boolean(),
    area_id: Yup.number().nullable()
});

const CompleteRegistrationForm = ({ municipalities }: { municipalities: Area[] }) => {
    const { push } = useRouter();
    const { dispatch } = useContext(AppContext);
    const { user } = useUser();
    const [initialFormState, setInitialFormState] = useState<RegistrationFormState>(initialValues);
    const [loginRedirect] = useLocalStorage(loginRedirectStorageKey, null);

    useEffect(() => {
        if (user) {
            setInitialFormState({
                first_name: user.first_name ?? '',
                last_name: user.last_name ?? '',
                area_id: user.area_id ?? null,
                newsletter: true
            });
        }
    }, [user]);
    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const response = await axios.post('/complete-registration', values);
            const user = response.data?.data;
            setUser(dispatch, mapUserProfile(user));
            if (loginRedirect) {
                push(loginRedirect);
            } else {
                push('/profil');
            }
            toast.success('Registracija uspešno zaključena!', { duration: 5000, icon: '🎉' });
            setSubmitting(false);
        } catch (e) {
            setSubmitting(false);
            toast.error('Registracija ni uspela. Poskusi ponovno.');
        }
    };

    return (
        <Formik
            enableReinitialize
            initialValues={initialFormState}
            validationSchema={schema}
            onSubmit={handleSubmit}
            validateOnBlur={true}
            validateOnChange={true}
        >
            {({ isSubmitting, handleSubmit, setFieldValue }) => (
                <form onSubmit={handleSubmit}>
                    <FormikInputField
                        name="first_name"
                        label="Ime"
                        type={'text'}
                        placeholder="Vnesi svoje ime"
                        autocomplete="first_name"
                    />
                    <div className="mt-4">
                        <FormikInputField
                            name="last_name"
                            label="Priimek"
                            type={'text'}
                            placeholder="Vnesi svoj priimek"
                            autocomplete="last_name"
                        />
                    </div>
                    <div className="mt-4">
                        <AutocompleteFormikField
                            name="area_id"
                            label="Občina prebivališča"
                            tooltip="Podatek o tvoji občini nam pomaga pri prikazovanju tebi relevantnih idej za izlet."
                            handleChange={(option) => setFieldValue('area_id', option?.value)}
                            options={municipalities.map((municipality) => ({
                                id: municipality.id,
                                name: municipality.name,
                                value: municipality.id
                            }))}
                        />
                    </div>
                    <div className="mt-6">
                        <FormikCheckboxField
                            name={'newsletter'}
                            label={
                                'Želim, da Kam za vikend na moj email občasno pošlje TOP ideje za izlet in me obvešča o novostih na portalu.'
                            }
                        />
                    </div>
                    <div className="flex flex-col mt-6">
                        <Button color={'secondary'} variant={'contained'} type={'submit'} disabled={isSubmitting}>
                            <span className="font-bold">Zaključi registracijo</span>
                        </Button>
                    </div>
                    <p className="mt-4 text-sm text-secondary text-center leading-tight">
                        Z registracijo se strinjam s{' '}
                        <Link href={documents.terms} target="_blank" className="underline">
                            pravili in pogoji
                        </Link>{' '}
                        ter{' '}
                        <Link href={documents.privacy} target="_blank" className="underline">
                            politiko zasebnosti
                        </Link>{' '}
                        portala Kam za vikend.
                    </p>
                </form>
            )}
        </Formik>
    );
};

export default CompleteRegistrationForm;
